import CachedIcon from "@mui/icons-material/Cached";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SelectDataTool from "pages/DataToolsPage/SelectDataTool";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  RunToolRequest,
} from "services/KnowfullyAdminClient";
import { convertToZonedDateTime } from "utils/dateUtils";

const statusColor = (
  status?: string,
): "default" | "warning" | "success" | "error" | "info" => {
  switch (status) {
    case "Running":
      return "warning";
    case "Completed":
      return "success";
    case "Failed":
      return "error";
    case "Enqueued":
      return "info";
    default:
      return "default";
  }
};

const DataToolsList = () => {
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const { dataToolsService, dataToolsRunUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  useListTitle("dataTools");

  const [selectedKey, setSelectedKey] = useState("");
  const [input, setInput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");

  const cells: TableCellType[] = [
    { accessor: "toolKey", label: t({ id: "tool" }), type: "text" },
    {
      accessor: "status",
      label: t({ id: "statusLabel" }),
      type: "node",
      noSort: true,
      View: ({ value }) => (
        <Chip
          label={value.status}
          color={statusColor(value.status)}
          size="small"
        />
      ),
    },
    { accessor: "createdOn", label: t({ id: "started" }), type: "text" },
    { accessor: "createdBy", label: t({ id: "by" }), type: "text" },
    { accessor: "completedOn", label: t({ id: "completed" }), type: "text" },
  ];

  const toolsQuery = useQuery(["dataToolsService.getTools"], () =>
    dataToolsService.getTools(),
  );

  const selectedTool = (toolsQuery.data?.data ?? []).find(
    (tool) => tool.id === selectedKey,
  );

  const runsQuery = useQuery(
    ["dataToolsService.getRuns", page, rowsPerPage, order, orderBy],
    () =>
      dataToolsService.getRuns(
        new ListRequestOfBaseFilter({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        }),
      ),
  );

  const totalRuns = runsQuery.data?.totalRecords ?? 0;

  const rows = (runsQuery.data?.data ?? []).map((r) => ({
    ...r,
    id: String(r.id),
    createdOn: convertToZonedDateTime(r.createdOn) ?? "",
    completedOn: convertToZonedDateTime(r.completedOn) ?? "",
  }));

  const handleRun = async () => {
    if (!selectedKey) {
      return;
    }
    setIsRunning(true);
    try {
      const request = new RunToolRequest({ input });
      const response = await dataToolsService.runTool(selectedKey, request);
      if (response.data?.id) {
        queryClient.invalidateQueries("dataToolsService.getRuns");
        navigate(dataToolsRunUrl(response.data.id));
      }
    } finally {
      setIsRunning(false);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries("dataToolsService.getRuns");
  };

  return (
    <Container>
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          {t({ id: "dataTools" })}
        </Typography>

        <SelectDataTool
          label={t({ id: "selectATool" })}
          value={selectedKey}
          onChange={(key) => {
            setSelectedKey(key ?? "");
            setInput("");
          }}
        />

        {selectedKey && (
          <>
            {selectedTool?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {selectedTool.description}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={t({ id: "inputOptional" })}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              sx={{ my: 2 }}
            />
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleRun}
                disabled={isRunning}
                startIcon={isRunning ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {t({ id: "run" })}
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="h6">{t({ id: "runHistory" })}</Typography>
        <Tooltip title={t({ id: "refresh" })}>
          <IconButton onClick={handleRefresh}>
            <CachedIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <LazyTable
        cells={cells}
        rows={rows}
        rowCount={totalRuns}
        loading={runsQuery.isFetching}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        onClick={(id) => navigate(dataToolsRunUrl(id))}
      />
    </Container>
  );
};

export default DataToolsList;
